import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Button from "@material-ui/core/Button";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import SendCredsIcon from "../../images/sendcreds.png";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { CircularProgress, IconButton, Snackbar, useMediaQuery } from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import {
  LoginInfoEmailTypes,
  UserType,
  useGetAdminConsoleLoginEmailForChamberMemberQuery,
  useGetSampleLoginInfoEmailQuery,
  useSendLoginInfoEmailToChamberMemberMutation,
  useSetNewTemporaryChamberMemberPasswordMutation,
} from "../../graphql/types";
import { CSVLink } from "react-csv";
import { useEnumStrings } from "../enumStrings";
import useGetChamberFromUserIdInAuth from "./Hooks/useGetChamberFromUserIdInAuth";
import { removePlusAddress } from "./utilities/removePlusAddress";
import {
  getCredentialEmailErrorMessage,
  hasCompleteCredentialEmailTarget,
} from "./credentialEmailError";
import { getPlainFailureReason } from "./utilities/userFacingError";
import { MEMBER_CONTACT_LOGINS_MESSAGE } from "../../apolloErrorReporting";
import { UserTypeContext } from "../../UserTypeContext";

type ModalType =
  | "memberTable"
  | "changeLoginEmailUsername"
  | "resetPassword"
  | "createChamberMember";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modal: {
      border: "unset",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    paper: {
      height: "100%",
      borderRadius: "5px",
      textAlign: "center",
      maxHeight: ({ modalType }: { modalType: ModalType }) =>
        modalType === "resetPassword" || modalType === "changeLoginEmailUsername"
          ? "700px"
          : modalType === "memberTable"
          ? "945px"
          : "680px",
      width: "740px",
      maxWidth: "100%",
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      backgroundColor: "#cfd8dc",
      padding: theme.spacing(4),
      position: "relative",
    },
    previewPaper: {
      borderRadius: "20px",
      textAlign: "center",
      maxHeight: "95vh",
      width: "966px",
      maxWidth: "95%",
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      backgroundColor: "#E6E6E6",
      padding: "0 30px 30px 30px",
      position: "relative",
      "@media (max-width: 768px)": {
        width: "100%",
        maxWidth: "95%",
        padding: "0 16px 16px",
        maxHeight: "95vh",
      },
    },
    yesButton: {
      margin: "10px",
      marginTop: "20px",
      width: "230px",
      backgroundColor: "#E1A731",
      color: "#e9e9e9",
      fontWeight: "bold",
      height: "40px",
    },
    okButton: {
      margin: "10px",
      marginTop: "20px",
      width: "230px",
      backgroundColor: "#37474f",
      color: "#e9e9e9",
      fontWeight: "bold",
      height: "40px",
    },
    resetPaper: {
      borderRadius: "20px",
      textAlign: "center",
      maxHeight: "90vh",
      width: "850px",
      maxWidth: "95%",
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      backgroundColor: "#FFFFFF",
      border: "1px solid #888888",
      padding: "14px 50px 30px",
      position: "relative",
      overflow: "auto",
      boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
      "@media (max-width: 600px)": {
        borderRadius: "16px",
        maxWidth: "calc(100% - 24px)",
        height: "auto",
        maxHeight: "90vh",
        padding: "20px 16px 18px",
      },
    },
    resetCloseButton: {
      position: "absolute",
      top: "14px",
      right: "14px",
      backgroundColor: "#ED1C24",
      color: "#fff",
      width: "30px",
      height: "30px",
      padding: 0,
      "&:hover": {
        backgroundColor: "#D41920",
      },
    },
    resetIconCircle: {
      width: "105px",
      minWidth: "105px",
      height: "105px",
      minHeight: "105px",
      borderRadius: "50%",
      backgroundColor: "#00B621",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "16px",
      flexShrink: 0,
      "@media (max-width: 600px)": {
        width: "72px",
        minWidth: "72px",
        height: "72px",
        minHeight: "72px",
        marginBottom: "12px",
      },
    },
    resetTitle: {
      fontFamily: "DM Sans, sans-serif",
      fontWeight: 700,
      fontSize: "22px",
      lineHeight: "150%",
      color: "#000000",
      marginBottom: "6px",
      "@media (max-width: 600px)": {
        fontSize: "18px",
      },
    },
    resetSubtitle: {
      fontFamily: "DM Sans, sans-serif",
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "150%",
      color: "#000000",
      textAlign: "left" as const,
      maxWidth: "940px",
      marginBottom: "32px",
      "@media (max-width: 600px)": {
        fontSize: "13px",
        marginBottom: "20px",
      },
    },
    resetPanelsContainer: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "24px",
      width: "100%",
      maxWidth: "960px",
      "@media (max-width: 600px)": {
        gridTemplateColumns: "1fr",
        gap: "16px",
      },
    },
    resetPanelLeft: {
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: "16px",
      padding: "24px",
      display: "flex",
      flexDirection: "column" as const,
      "@media (max-width: 600px)": {
        padding: "16px",
      },
    },
    resetPanelRight: {
      border: "1px solid #000000",
      borderRadius: "16px",
      padding: "24px",
      display: "flex",
      flexDirection: "column" as const,
      "@media (max-width: 600px)": {
        padding: "16px",
      },
    },
    resetPanelTitleGold: {
      fontFamily: "DM Sans, sans-serif",
      fontWeight: 700,
      fontSize: "20px",
      lineHeight: "150%",
      color: theme.palette.primary.main,
      textAlign: "left" as const,
      marginBottom: "16px",
      whiteSpace: "pre-line" as const,
      "@media (max-width: 600px)": {
        fontSize: "16px",
        marginBottom: "12px",
      },
    },
    resetPanelTitleBlack: {
      fontFamily: "DM Sans, sans-serif",
      fontWeight: 700,
      fontSize: "20px",
      lineHeight: "150%",
      color: "#000000",
      textAlign: "left" as const,
      marginBottom: "16px",
      whiteSpace: "pre-line" as const,
      "@media (max-width: 600px)": {
        fontSize: "16px",
        marginBottom: "12px",
      },
    },
    resetPanelDesc: {
      fontFamily: "DM Sans, sans-serif",
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "170%",
      color: "#000000",
      textAlign: "left" as const,
      flex: 1,
      marginBottom: "20px",
      "@media (max-width: 600px)": {
        fontSize: "13px",
        lineHeight: "160%",
        marginBottom: "14px",
      },
    },
    resetDownloadButton: {
      backgroundColor: "#E1A731",
      color: "#FFFFFF",
      fontFamily: "DM Sans, sans-serif",
      fontWeight: 600,
      fontSize: "14px",
      lineHeight: "150%",
      borderRadius: "12px",
      padding: "12px 40px",
      textTransform: "uppercase" as const,
      boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
      "&:hover": {
        backgroundColor: "#C99028",
      },
      "@media (max-width: 600px)": {
        width: "100%",
        padding: "12px 16px",
      },
    },
    resetSendEmailButton: {
      backgroundColor: "#FFFFFF",
      color: "#000000",
      fontFamily: "DM Sans, sans-serif",
      fontWeight: 600,
      fontSize: "14px",
      lineHeight: "150%",
      borderRadius: "12px",
      padding: "12px 40px",
      textTransform: "uppercase" as const,
      border: "1px solid #000000",
      boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
      "&:hover": {
        backgroundColor: "#F5F5F5",
      },
      "@media (max-width: 600px)": {
        width: "100%",
        padding: "12px 16px",
      },
    },
    resetPreviewEmailButton: {
      backgroundColor: "#000000",
      color: "#FFFFFF",
      fontFamily: "DM Sans, sans-serif",
      fontWeight: 600,
      fontSize: "14px",
      lineHeight: "150%",
      borderRadius: "12px",
      padding: "12px 40px",
      textTransform: "uppercase" as const,
      border: "1px solid #000000",
      boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
      "@media (max-width: 600px)": {
        width: "100%",
        padding: "12px 16px",
      },
      "&:hover": {
        backgroundColor: "#333333",
      },
    },
  })
);

const DownloadLoginInfoConfirmationModal = ({
  open,
  handleClose,
  memberAdminPortalEmail,
  memberCompnayName,
  newPassword,
  modalType,
}: {
  open: boolean;
  handleClose: () => void;
  memberAdminPortalEmail: string;
  memberCompnayName: string;
  newPassword: string;
  modalType: ModalType;
}): ReactElement => {
  const classes = useStyles({ modalType: modalType });
  const isMobile = useMediaQuery("(max-width: 600px)");

  const enumStrings = useEnumStrings();

  const triggerCsvDownload = useCallback(() => {
    if (!memberAdminPortalEmail || !newPassword) return;
    const emailHeader = enumStrings.sendLoginModal.memberEmail;
    const passwordHeader = enumStrings.sendLoginModal.memberPassword;
    const csvContent = `${emailHeader},${passwordHeader}\n${memberAdminPortalEmail},${newPassword}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${memberCompnayName} member dashboard credentials.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [memberAdminPortalEmail, newPassword, memberCompnayName, enumStrings]);

  // Auto-trigger CSV download when modal opens
  useEffect(() => {
    if (open && newPassword) {
      const timer = setTimeout(() => {
        triggerCsvDownload();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open, newPassword, triggerCsvDownload]);

  return (
    <Modal className={classes.modal} open={open}>
      <div className={classes.resetPaper} style={{ maxWidth: isMobile ? "calc(100% - 48px)" : "500px", padding: isMobile ? "28px 24px" : "36px 40px", height: "auto", maxHeight: "85vh" }}>
        <IconButton
          className={classes.resetCloseButton}
          onClick={handleClose}
        >
          <CloseIcon style={{ fontSize: "20px" }} />
        </IconButton>

        {/* Green checkmark */}
        <div className={classes.resetIconCircle}>
          <CheckIcon style={{ fontSize: "44px", color: "#FFFFFF" }} />
        </div>

        {/* Title */}
        <div className={classes.resetTitle} style={{ marginBottom: "16px" }}>
          {enumStrings.sendLoginModal.downloadCredentials}
        </div>

        {/* Description */}
        <div className={classes.resetSubtitle} style={{ marginBottom: "28px" }}>
          {enumStrings.sendLoginModal.memberTitle}
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: isMobile ? "10px" : "16px", justifyContent: "center", flexDirection: isMobile ? "column" : "row", width: isMobile ? "100%" : "auto" }}>
          <Button className={classes.resetDownloadButton} variant="contained" onClick={triggerCsvDownload}>
            {enumStrings.sendLoginModal.download}
          </Button>

          <Button
            className={classes.resetPreviewEmailButton}
            onClick={handleClose}
            variant="contained"
          >
            {enumStrings.sendLoginModal.close}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const EmailLoginInfoConfirmationModal = ({
  open,
  handleClose,
  modalType,
}: {
  open: boolean;
  handleClose: () => void;
  modalType: ModalType;
}) => {
  const classes = useStyles({ modalType: modalType });
  const isMobile = useMediaQuery("(max-width: 600px)");
  const enumStrings = useEnumStrings();

  return (
    <Modal className={classes.modal} open={open}>
      <div className={classes.resetPaper} style={{ maxWidth: isMobile ? "calc(100% - 48px)" : "500px", padding: isMobile ? "28px 24px" : "36px 40px", height: "auto", maxHeight: "85vh" }}>
        <IconButton
          className={classes.resetCloseButton}
          onClick={handleClose}
        >
          <CloseIcon style={{ fontSize: "20px" }} />
        </IconButton>

        {/* Green checkmark */}
        <div className={classes.resetIconCircle}>
          <CheckIcon style={{ fontSize: "44px", color: "#FFFFFF" }} />
        </div>

        {/* Title */}
        <div className={classes.resetTitle} style={{ marginBottom: "16px" }}>
          {enumStrings.sendLoginModal.sendEmail}
        </div>

        {/* Description */}
        <div className={classes.resetSubtitle} style={{ marginBottom: "28px" }}>
          {enumStrings.sendLoginModal.memberTitle2}
        </div>

        {/* Close button */}
        <Button
          className={classes.resetPreviewEmailButton}
          onClick={handleClose}
          variant="contained"
        >
          {enumStrings.shopLocal.createShopLocalCampaign.close}
        </Button>
      </div>
    </Modal>
  );
};

export const SampleLoginInfoEmailModal = ({
  open,
  handleClose,
  emailHtml,
  modalType,
  memberName,
  memberEmail,
  chamberName,
  chamberEmail,
  adminContactName,
}: {
  open: boolean;
  handleClose: () => void;
  emailHtml: null | undefined | string;
  modalType: ModalType;
  memberName?: string;
  memberEmail?: string;
  chamberName?: string;
  chamberEmail?: string;
  adminContactName?: string;
}) => {
  const classes = useStyles({ modalType: modalType });
  const enumStrings = useEnumStrings();
  const emailContentRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = useCallback(() => {
    if (emailContentRef.current) {
      const text = emailContentRef.current.innerText;
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }, []);

  const previewEmailStrings = (enumStrings as any).manageMemberCredentials?.previewEmailDialog;
  const sendCredsPreviewStrings = (enumStrings as any).manageMemberCredentials?.sendCredsPreviewEmail;

  const renderLocalPreview = (modalType === "resetPassword" || modalType === "memberTable") && previewEmailStrings;

  return (
    <Modal className={classes.modal} open={open}>
      <div className={classes.previewPaper}>
        {/* Header: Back arrow + Title + Close button */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            padding: "16px 0",
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={handleClose}
            style={{
              width: "38px",
              height: "38px",
              padding: 0,
            }}
          >
            <ArrowBackIcon style={{ fontSize: "28px", color: "#000000" }} />
          </IconButton>
          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 700,
              fontSize: "25px",
              lineHeight: "200%",
              color: "#000000",
              textAlign: "center",
            }}
          >
            {previewEmailStrings?.title || enumStrings.sendLoginModal.previewEmail}
          </div>
          <IconButton
            onClick={handleClose}
            style={{
              width: "30px",
              height: "30px",
              backgroundColor: "#ED1C24",
              color: "#fff",
              padding: 0,
            }}
          >
            <CloseIcon style={{ fontSize: "18px" }} />
          </IconButton>
        </div>

        {/* Email content area */}
        <div
          ref={emailContentRef}
          style={{
            textAlign: "left",
            padding: "38px 20px",
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #000000",
            overflow: "auto",
            flex: "1 1 auto",
            minHeight: 0,
            width: "100%",
            fontFamily: "Inter, sans-serif",
            fontWeight: 400,
            fontSize: "18px",
            lineHeight: "200%",
            color: "#000000",
          }}
        >
          {renderLocalPreview ? (
            modalType === "memberTable" && sendCredsPreviewStrings ? (
              <div style={{ fontFamily: "Inter, sans-serif", fontSize: "18px", lineHeight: "200%", color: "#000000" }}>
                <p><strong>Subject:</strong> {sendCredsPreviewStrings.subject}</p>
                <p>{sendCredsPreviewStrings.greeting.replace("{memberName}", memberName || "{Insert Company Administrator Contact Name}")}</p>
                <p>{sendCredsPreviewStrings.introParagraph.replace("{chamberName}", chamberName || "{Insert Chamber Name}")}</p>
                <p>{sendCredsPreviewStrings.contactParagraph}</p>
                <p>
                  {sendCredsPreviewStrings.dashboardAccessParagraph}{" "}
                  <a href={`https://${sendCredsPreviewStrings.dashboardUrl}`} style={{ color: "#E1A731", textDecoration: "underline" }}>
                    {sendCredsPreviewStrings.dashboardUrl}
                  </a>.
                </p>
                <p>
                  <strong style={{ textDecoration: "underline" }}>{sendCredsPreviewStrings.loginCredentialsHeader}</strong>
                  <br />
                  <strong>{sendCredsPreviewStrings.emailUsername}</strong> {memberEmail || "{Administrator Contact Email}"}
                  <br />
                  <strong>{sendCredsPreviewStrings.tempPassword}</strong> {sendCredsPreviewStrings.insertTempPassword}
                </p>
                <p>{sendCredsPreviewStrings.employeeEncouragement}</p>
                <p>{sendCredsPreviewStrings.contactInfo}</p>
                <p>{sendCredsPreviewStrings.closingMessage}</p>
                <p>
                  {sendCredsPreviewStrings.sincerely}
                  <br />
                  {adminContactName || "{Insert Administrator Contact Name}"}
                  <br />
                  {chamberEmail || "{Insert Chamber Administrator Contact Email}"}
                </p>
              </div>
            ) : modalType === "resetPassword" ? (
              <div style={{ fontFamily: "Inter, sans-serif", fontSize: "18px", lineHeight: "200%", color: "#000000" }}>
                <p><strong>Subject:</strong> {previewEmailStrings.subject}</p>
                <p>{previewEmailStrings.greeting.replace("{memberName}", memberName || "{Company Administrator Name}")}</p>
                <p>
                  {previewEmailStrings.introMessage.replace("{chamberName}", memberName || "{INSERT MEMBER COMPANY NAME}")}
                </p>
                <p>
                  {previewEmailStrings.dashboardAccessParagraph}{" "}
                  <a href={`https://${previewEmailStrings.dashboardUrl}`} style={{ color: "#000", textDecoration: "underline" }}>
                    {previewEmailStrings.dashboardUrl}
                  </a>.
                </p>
                <p>
                  {previewEmailStrings.loginInstructions}
                  <br />
                  {previewEmailStrings.emailUsername} {memberEmail || "{Insert Email Username}"}
                  <br />
                  {previewEmailStrings.tempPassword} {previewEmailStrings.insertTempPassword}
                </p>
                <p>
                  {previewEmailStrings.newPasswordPrompt}
                  <br />
                  {previewEmailStrings.shareCredentials}
                </p>
                <p>{previewEmailStrings.forgotPasswordNote}</p>
                <p>{previewEmailStrings.contactInfo.replace("{chamberName}", chamberName || "{INSERT CHAMBER NAME}")}</p>
                <p>
                  {previewEmailStrings.sincerely}
                  <br />
                  {adminContactName || "{Insert Administrator Contact Name}"}
                  <br />
                  {chamberEmail || "{Insert Chamber Administrator Contact Email}"}
                </p>
              </div>
            ) : (
              <div style={{ fontFamily: "Inter, sans-serif", fontSize: "18px", lineHeight: "200%", color: "#000000" }}>
                <p>{previewEmailStrings.greeting.replace("{memberName}", memberName || "{Member Name}")}</p>
                <p>{previewEmailStrings.passwordResetMessage.replace("{chamberName}", chamberName || "{Chamber Name}")}</p>
                <p style={{ fontWeight: 700 }}>{previewEmailStrings.accessTitle}</p>
                <p>{previewEmailStrings.accessOption1}</p>
                <p style={{ fontWeight: 700 }}>{previewEmailStrings.or}</p>
                <p>
                  {previewEmailStrings.accessOption2}{" "}
                  <a href={`https://${previewEmailStrings.dashboardUrl}`} style={{ color: "#000", textDecoration: "underline" }}>
                    {previewEmailStrings.dashboardUrl}
                  </a>
                </p>
                <p>{previewEmailStrings.resetLoginInstructions}</p>
                <ul style={{ paddingLeft: "24px", listStyleType: "disc" }}>
                  <li>{previewEmailStrings.emailUsername} {memberEmail || "{email}"}</li>
                  <li>{previewEmailStrings.tempPassword} {previewEmailStrings.insertTempPassword}</li>
                </ul>
                <p>{previewEmailStrings.bestRegards}</p>
                <p>{chamberName || "{Chamber Name}"}</p>
              </div>
            )
          ) : (
            <>
              <style>{`.email-preview-no-bold, .email-preview-no-bold * { font-family: Inter, sans-serif !important; font-weight: 400 !important; font-size: 18px !important; line-height: 200% !important; color: #000000 !important; } .email-preview-no-bold a { text-decoration: underline !important; }`}</style>
              <div
                dangerouslySetInnerHTML={{ __html: emailHtml || "" }}
                className="email-preview-no-bold"
                style={{
                  maxWidth: "100%",
                  overflowX: "hidden",
                  wordBreak: "break-word",
                }}
              />
            </>
          )}
        </div>

        {/* Copy Email Button */}
        <Button
          onClick={handleCopyEmail}
          variant="contained"
          style={{
            marginTop: "16px",
            backgroundColor: copied ? "#4CAF50" : "#000000",
            color: "#FFFFFF",
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: "14px",
            textTransform: "uppercase",
            borderRadius: "8px",
            padding: "10px 32px",
            flexShrink: 0,
          }}
        >
          {copied ? "Copied!" : "Copy Email"}
        </Button>
      </div>
    </Modal>
  );
};

const SendLoginInformationModal = ({
  open,
  handleClose,
  redirectToMemberProfile,
  memberCompanyName,
  memberAdminPortalEmail,
  chamberMemberId,
  type,
  credentialsSentAtUtcMilli,
  isSuspended,
}: {
  open: boolean;
  handleClose: () => void;
  redirectToMemberProfile?: () => void;
  memberCompanyName: string;
  memberAdminPortalEmail: string;
  chamberMemberId: string | null | undefined;
  type: ModalType;
  isSuspended: boolean;
  credentialsSentAtUtcMilli?: number | null | undefined;
}): ReactElement => {
  const chamberInfo = useGetChamberFromUserIdInAuth();
  const enumStrings = useEnumStrings();
  const classes = useStyles({ modalType: type });
  const isMobile = useMediaQuery("(max-width: 600px)");

  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);

  // A Chamber Member Pro member on per-contact logins gets credentials from
  // Update Member > Member Contacts; the backend refuses these tools for it.
  const {
    state: { userType },
  } = useContext(UserTypeContext);
  const { data: loginModeData, loading: loginModeLoading } =
    useGetAdminConsoleLoginEmailForChamberMemberQuery({
      variables: { chamberMemberId: chamberMemberId || "" },
      skip: !open || !chamberMemberId || userType !== UserType.ChamberAdmin,
      fetchPolicy: "network-only",
    });
  const loginsBelongToContacts = Boolean(
    loginModeData?.getAdminConsoleLoginEmailForChamberMember
      ?.memberDashboardLoginsBelongToContacts
  );

  // Reset states when modal opens
  useEffect(() => {
    if (open) {
      setEmailSentSuccess(false);
      setEmailErrorMessage("");
    }
  }, [open]);

  const [
    downloadLoginInfoConfirmationModalOpen,
    setDownloadLoginInfoConfirmationModalOpen,
  ] = useState(false);
  const closeDownloadLoginInfoConfirmationModal = useCallback(() => {
    setDownloadLoginInfoConfirmationModalOpen(false);
    handleClose();
  }, [handleClose]);

  const [
    emailLoginInfoConfirmationModalOpen,
    setEmailLoginInfoConfirmationModalOpen,
  ] = useState(false);
  const closeEmailLoginInfoConfirmationModal = useCallback(() => {
    setEmailLoginInfoConfirmationModalOpen(false);
    handleClose();
  }, [handleClose]);

  const [sampleLoginInfoEmailModalOpen, setSampleLoginInfoEmailModalOpen] =
    useState(false);
  const closeSampleLoginInfoEmailModal = useCallback(() => {
    setSampleLoginInfoEmailModalOpen(false);
  }, []);

  const [setNewTemporaryChamberMemberPassowrd] =
    useSetNewTemporaryChamberMemberPasswordMutation();

  const executeDownloadLoginInfo = useCallback(async () => {
    const fallbackErrorMessage =
      enumStrings.toastMessages.failedToGeneratePassword;
    setLoading(true);

    try {
      const { data } = await setNewTemporaryChamberMemberPassowrd({
        variables: {
          adminConsoleLoginEmail: memberAdminPortalEmail || "",
          chamberMemberId: chamberMemberId || "",
        },
      });
      const result = data?.setNewTemporaryChamberMemberPassword;

      // Never offer a download without a password; show the saved reason.
      if (!result?.password) {
        setEmailErrorMessage(
          getPlainFailureReason(result?.failureReason) || fallbackErrorMessage
        );
        return;
      }

      setNewPassword(result.password);
      setDownloadLoginInfoConfirmationModalOpen(true);
    } catch (error) {
      setEmailErrorMessage(
        getCredentialEmailErrorMessage({
          error,
          fallbackMessage: fallbackErrorMessage,
        })
      );
    } finally {
      setLoading(false);
    }
  }, [
    chamberMemberId,
    enumStrings.toastMessages.failedToGeneratePassword,
    memberAdminPortalEmail,
    setNewTemporaryChamberMemberPassowrd,
  ]);

  const csvData = useMemo(
    () => [
      [
        enumStrings.sendLoginModal.memberEmail,
        enumStrings.sendLoginModal.memberPassword,
      ],
      [memberAdminPortalEmail, newPassword],
    ],
    [
      enumStrings.sendLoginModal.memberEmail,
      enumStrings.sendLoginModal.memberPassword,
      memberAdminPortalEmail,
      newPassword,
    ]
  );

  const [sendLoginInfoEmailToChamberMember] =
    useSendLoginInfoEmailToChamberMemberMutation();

  const executeSendLoginInfoEmail = useCallback(async () => {
    const fallbackErrorMessage = enumStrings.toastMessages.failedToSendEmail;
    const normalizedAdminConsoleLoginEmail = memberAdminPortalEmail.trim();
    const normalizedChamberMemberId = chamberMemberId?.trim() || "";
    if (
      !hasCompleteCredentialEmailTarget({
        adminConsoleLoginEmail: normalizedAdminConsoleLoginEmail,
        chamberMemberId: normalizedChamberMemberId,
      })
    ) {
      setEmailSentSuccess(false);
      setEmailErrorMessage(fallbackErrorMessage);
      return;
    }

    setLoading(true);

    try {
      let apiType = LoginInfoEmailTypes.BrandNew;

      if (type === "changeLoginEmailUsername") {
        apiType = LoginInfoEmailTypes.ChangeEmailUsername;
      }
      if (type === "resetPassword") {
        apiType = LoginInfoEmailTypes.ResetPassword;
      }

      const { data } = await sendLoginInfoEmailToChamberMember({
        variables: {
          adminConsoleLoginEmail: normalizedAdminConsoleLoginEmail,
          chamberMemberId: normalizedChamberMemberId,
          type: apiType,
        },
        // A later list-refresh failure must not turn a confirmed credential
        // email result into a failure or invite another password rotation.
        refetchQueries: ["getChamberMembers", "getAllChamberMembers"],
      });

      if (
        data?.sendLoginInfoEmailToChamberMember?.loginInfoEmailSentSuccessfully
      ) {
        setEmailErrorMessage("");
        setEmailSentSuccess(true);
      } else {
        setEmailErrorMessage(fallbackErrorMessage);
        setEmailSentSuccess(false);
      }
    } catch (error) {
      setEmailErrorMessage(
        getCredentialEmailErrorMessage({
          error,
          fallbackMessage: fallbackErrorMessage,
        })
      );
      setEmailSentSuccess(false);
    }

    setLoading(false);
  }, [
    chamberMemberId,
    enumStrings.toastMessages.failedToSendEmail,
    memberAdminPortalEmail,
    sendLoginInfoEmailToChamberMember,
    type,
  ]);

  const sampleType: LoginInfoEmailTypes = useMemo(() => {
    if (type === "changeLoginEmailUsername") {
      return LoginInfoEmailTypes.ChangeEmailUsername;
    }
    if (type === "resetPassword") {
      return LoginInfoEmailTypes.ResetPassword;
    }
    return LoginInfoEmailTypes.BrandNew;
  }, [type]);

  const { data: getSampleLoginEmail } = useGetSampleLoginInfoEmailQuery({
    variables: {
      type: sampleType,
      chamberMemberId: chamberMemberId || "",
    },
  });

  const modalTitle = useMemo(() => {
    if (type === "memberTable") {
      if (credentialsSentAtUtcMilli) {
        return enumStrings.sendLoginModal.resendLoginCredentials;
      }
      return enumStrings.sendLoginModal.sendLoginCredentials;
    }

    if (type === "changeLoginEmailUsername") {
      return enumStrings.sendLoginModal.changeSuccess;
    }

    if (type === "resetPassword") {
      return enumStrings.sendLoginModal.newTempPassword;
    }

    if (type === "createChamberMember") {
      return enumStrings.loginInormationModal.titles.member;
    }

    return "";
  }, [
    credentialsSentAtUtcMilli,
    enumStrings.loginInormationModal.titles.member,
    enumStrings.sendLoginModal.changeSuccess,
    enumStrings.sendLoginModal.newTempPassword,
    enumStrings.sendLoginModal.resendLoginCredentials,
    enumStrings.sendLoginModal.sendLoginCredentials,
    type,
  ]);

  return (
    <>
      <SampleLoginInfoEmailModal
        open={sampleLoginInfoEmailModalOpen}
        handleClose={closeSampleLoginInfoEmailModal}
        emailHtml={getSampleLoginEmail?.getSampleLoginInfoEmail?.emailHtml}
        modalType={type}
        memberName={memberCompanyName}
        memberEmail={memberAdminPortalEmail}
        chamberName={chamberInfo?.name || ""}
        adminContactName={chamberInfo?.supportContactName || ""}
        chamberEmail={chamberInfo?.supportContactEmail || ""}
      />
      <DownloadLoginInfoConfirmationModal
        open={downloadLoginInfoConfirmationModalOpen}
        handleClose={closeDownloadLoginInfoConfirmationModal}
        memberAdminPortalEmail={memberAdminPortalEmail}
        memberCompnayName={memberCompanyName}
        newPassword={newPassword}
        modalType={type}
      />
      <EmailLoginInfoConfirmationModal
        open={emailLoginInfoConfirmationModalOpen}
        handleClose={closeEmailLoginInfoConfirmationModal}
        modalType={type}
      />
      {type === "resetPassword" || type === "changeLoginEmailUsername" || type === "memberTable" ? (
        <Modal
          className={classes.modal}
          style={{
            ...(sampleLoginInfoEmailModalOpen ||
            downloadLoginInfoConfirmationModalOpen ||
            emailLoginInfoConfirmationModalOpen
              ? { display: "none" }
              : {}),
          }}
          open={open}
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <div className={classes.resetPaper} style={emailSentSuccess ? { maxWidth: isMobile ? "calc(100% - 48px)" : "500px", maxHeight: "fit-content", height: "auto" } : {}}>
            <IconButton
              className={classes.resetCloseButton}
              onClick={() => {
                setEmailSentSuccess(false);
                handleClose();
              }}
            >
              <CloseIcon style={{ fontSize: "20px" }} />
            </IconButton>

            {loading || loginModeLoading ? (
              <div
                style={{
                  display: "flex",
                  flex: 1,
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "400px",
                }}
              >
                <CircularProgress style={{ color: "#E1A731" }} />
              </div>
            ) : emailSentSuccess ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: isMobile ? "24px 16px 28px" : "40px 30px 50px",
                }}
              >
                <div
                  style={{
                    width: isMobile ? "60px" : "70px",
                    height: isMobile ? "60px" : "70px",
                    borderRadius: "50%",
                    backgroundColor: "#22C55E",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: isMobile ? "16px" : "24px",
                  }}
                >
                  <CheckIcon style={{ fontSize: isMobile ? "34px" : "40px", color: "#FFFFFF" }} />
                </div>
                <div style={{
                  fontFamily: "DM Sans, sans-serif",
                  fontWeight: 700,
                  fontSize: isMobile ? "16px" : "24px",
                  textAlign: "center",
                  color: "#000000",
                  lineHeight: "140%",
                }}>
                  {type === "memberTable"
                    ? enumStrings.sendLoginModal.credentialEmailSentSuccess
                    : enumStrings.manageMembersPage.emailSentSuccess}
                </div>
              </div>
            ) : isSuspended || loginsBelongToContacts ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "40px 25px",
                }}
              >
                <h3
                  style={{
                    textAlign: "left",
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    marginBottom: "24px",
                  }}
                >
                  {isSuspended
                    ? enumStrings.sendLoginModal.description
                    : MEMBER_CONTACT_LOGINS_MESSAGE}
                </h3>
                <Button
                  className={classes.yesButton}
                  onClick={handleClose}
                  variant="contained"
                >
                  {enumStrings.shopLocal.createShopLocalCampaign.close}
                </Button>
              </div>
            ) : (
              <>
                {/* Send credentials icon */}
                <div className={classes.resetIconCircle}>
                  <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="30" cy="30" r="25" stroke="#FFFFFF" strokeWidth="4" fill="none" /><path d="M18 30L26 38L42 22" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" /></svg>
                </div>

                {/* Title */}
                <div className={classes.resetTitle}>
                  {type === "memberTable" || type === "changeLoginEmailUsername"
                    ? enumStrings.sendLoginModal.sendLoginCredentials
                    : enumStrings.sendLoginModal.newTempPassword}
                </div>

                {/* Subtitle */}
                <div className={classes.resetSubtitle}>
                  {type === "memberTable"
                    ? enumStrings.sendLoginModal.sendCredentialsSubtitle
                    : type === "changeLoginEmailUsername"
                    ? enumStrings.sendLoginModal.changeLoginEmailSubtitle
                    : enumStrings.sendLoginModal.resetPasswordSubtitle.replaceAll("{memberCompanyName}", memberCompanyName)}
                </div>

                {type === "memberTable" || type === "resetPassword" || type === "changeLoginEmailUsername" ? (
                  <div style={{ border: "1px solid #000000", borderRadius: "16px", padding: isMobile ? "16px" : "30px", width: "100%", maxWidth: "913px", display: "flex", flexDirection: "column" as const, gap: "10px" }}>
                    {/* Manual Section */}
                    <div style={{ display: "flex", flexDirection: "column" as const, alignItems: "flex-start", gap: isMobile ? "14px" : "20px", width: "100%" }}>
                      <div style={{ fontFamily: "DM Sans, sans-serif", fontWeight: 700, fontSize: isMobile ? "15px" : "18px", lineHeight: "150%", color: "#000000", textAlign: "left" as const }}>
                        {type === "resetPassword" ? enumStrings.sendLoginModal.option2HeaderReset : enumStrings.sendLoginModal.option2Header}
                      </div>
                      <div style={{ fontFamily: "DM Sans, sans-serif", fontWeight: 400, fontSize: isMobile ? "13px" : "14px", lineHeight: isMobile ? "160%" : "170%", color: "#000000", textAlign: "left" as const }}>
                        {(() => {
                          const text = (type === "resetPassword" ? enumStrings.sendLoginModal.manuallyDescReset : enumStrings.sendLoginModal.manuallyDescSendCreds).replaceAll("{memberCompanyName}", memberCompanyName);
                          const boldTerms = ["\"Download Credentials\"", "\"DOWNLOAD CREDENTIALS\"", "\"PREVIEW EMAIL\""];
                          const regex = new RegExp(`(${boldTerms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
                          const parts = text.split(regex);
                          return parts.map((part: string, i: number) => {
                            if (part === "\n\n" || part.includes("\n\n")) {
                              const subParts = part.split("\n\n");
                              return subParts.map((sp: string, j: number) => (
                                <React.Fragment key={`${i}-${j}`}>
                                  {j > 0 && <><br /><br /></>}
                                  {sp}
                                </React.Fragment>
                              ));
                            }
                            if (boldTerms.includes(part)) return <strong key={i}>{part}</strong>;
                            return <React.Fragment key={i}>{part}</React.Fragment>;
                          });
                        })()}
                      </div>
                      <div style={{ display: "flex", gap: isMobile ? "12px" : "25px", justifyContent: "flex-start", flexDirection: isMobile ? "column" : "row", width: isMobile ? "100%" : "auto" }}>
                        <Button className={classes.resetDownloadButton} onClick={executeDownloadLoginInfo} variant="contained">
                          {enumStrings.sendLoginModal.downloadCredentials}
                        </Button>
                        <Button className={classes.resetPreviewEmailButton} onClick={() => setSampleLoginInfoEmailModalOpen(true)} variant="contained">
                          {enumStrings.sendLoginModal.previewEmail}
                        </Button>
                      </div>
                    </div>
                    {/* Automatic Section */}
                    <div style={{ display: "flex", flexDirection: "column" as const, alignItems: "flex-start", gap: isMobile ? "14px" : "20px", width: "100%" }}>
                      <div style={{ fontFamily: "DM Sans, sans-serif", fontWeight: 700, fontSize: isMobile ? "15px" : "18px", lineHeight: "150%", color: "#000000", textAlign: "left" as const }}>
                        {type === "resetPassword" ? enumStrings.sendLoginModal.option1HeaderReset : enumStrings.sendLoginModal.option1Header}
                      </div>
                      <div style={{ fontFamily: "DM Sans, sans-serif", fontWeight: 400, fontSize: isMobile ? "13px" : "14px", lineHeight: isMobile ? "160%" : "170%", color: "#000000", textAlign: "left" as const }}>
                        {(() => {
                          const memberEmail = memberAdminPortalEmail || "{email}";
                          const text = (type === "resetPassword" ? enumStrings.sendLoginModal.automaticallyDescReset : enumStrings.sendLoginModal.automaticallyDescSendCreds).replace("{memberEmail}", memberEmail).replaceAll("{memberCompanyName}", memberCompanyName);
                          const boldTerms = ["\"SEND AUTO EMAIL,\"", "\"SEND AUTO EMAIL\"", "Preview Email", "hello@chamberperksapp.com", "noreply@chamberperksapp.com", memberEmail];
                          const uniqueTerms = Array.from(new Set(boldTerms));
                          const regex = new RegExp(`(${uniqueTerms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
                          const parts = text.split(regex);
                          return parts.map((part: string, i: number) => {
                            if (part === "hello@chamberperksapp.com" || part === "noreply@chamberperksapp.com") return <span key={i} style={{ textDecoration: "underline" }}>{part}</span>;
                            if (uniqueTerms.includes(part)) return <strong key={i}>{part}</strong>;
                            return <React.Fragment key={i}>{part}</React.Fragment>;
                          });
                        })()}
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-start", width: isMobile ? "100%" : "auto" }}>
                        <Button
                          className={classes.resetSendEmailButton}
                          style={{ backgroundColor: "#00B621", color: "#FFFFFF", border: "1px solid #00B621", boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)" }}
                          onClick={executeSendLoginInfoEmail}
                          variant="contained"
                        >
                          {enumStrings.sendLoginModal.sendAutoEmail || "SEND AUTO EMAIL"}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={classes.resetPanelsContainer}>
                    <div className={classes.resetPanelLeft}>
                      <div className={classes.resetPanelTitleGold}>
                        {type === "changeLoginEmailUsername" ? enumStrings.sendLoginModal.option2Header : enumStrings.sendLoginModal.option2HeaderReset}
                      </div>
                      <div className={classes.resetPanelDesc}>
                        {(() => {
                          const text = (type === "changeLoginEmailUsername" ? enumStrings.sendLoginModal.manuallyDescChangeEmail : enumStrings.sendLoginModal.manuallyDescReset).replaceAll("{memberCompanyName}", memberCompanyName);
                          const boldTerms = ["'Download Credentials'", "'DOWNLOAD CREDENTIALS'", "'PREVIEW EMAIL'", "Preview Email"];
                          const regex = new RegExp(`(${boldTerms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
                          const parts = text.split(regex);
                          return parts.map((part: string, i: number) =>
                            boldTerms.includes(part) ? <strong key={i}>{part}</strong> : <React.Fragment key={i}>{part}</React.Fragment>
                          );
                        })()}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" }}>
                        <Button className={classes.resetDownloadButton} onClick={executeDownloadLoginInfo} variant="contained">
                          {enumStrings.sendLoginModal.downloadCredentials}
                        </Button>
                      </div>
                    </div>
                    <div className={classes.resetPanelRight}>
                      <div className={classes.resetPanelTitleBlack}>
                        {type === "changeLoginEmailUsername" ? enumStrings.sendLoginModal.option1Header : enumStrings.sendLoginModal.option1HeaderReset}
                      </div>
                      <div className={classes.resetPanelDesc}>
                        {(() => {
                          const memberEmail = memberAdminPortalEmail || "{email}";
                          const text = (type === "changeLoginEmailUsername" ? enumStrings.sendLoginModal.automaticallyDescChangeEmail : enumStrings.sendLoginModal.automaticallyDescReset).replace("{memberEmail}", memberEmail).replaceAll("{memberCompanyName}", memberCompanyName);
                          const boldTerms = ["'Send Email'", "'SEND EMAIL,'", "Preview Email", "hello@chamberperksapp.com", "noreply@chamberperksapp.com", memberEmail];
                          const uniqueTerms = Array.from(new Set(boldTerms));
                          const regex = new RegExp(`(${uniqueTerms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
                          const parts = text.split(regex);
                          return parts.map((part: string, i: number) => {
                            if (part === "hello@chamberperksapp.com" || part === "noreply@chamberperksapp.com") return <span key={i} style={{ textDecoration: "underline" }}>{part}</span>;
                            if (uniqueTerms.includes(part)) return <strong key={i}>{part}</strong>;
                            return <React.Fragment key={i}>{part}</React.Fragment>;
                          });
                        })()}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" }}>
                        <Button className={classes.resetPreviewEmailButton} onClick={() => setSampleLoginInfoEmailModalOpen(true)} variant="contained">
                          {enumStrings.sendLoginModal.previewEmail}
                        </Button>
                        <Button className={classes.resetSendEmailButton} onClick={executeSendLoginInfoEmail} variant="contained">
                          {enumStrings.sendLoginModal.sendEmail}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </Modal>
      ) : (
        <Modal
          className={classes.modal}
          style={{
            ...(sampleLoginInfoEmailModalOpen ||
            downloadLoginInfoConfirmationModalOpen ||
            emailLoginInfoConfirmationModalOpen
              ? { display: "none" }
              : {}),
          }}
          open={open}
          onClose={undefined}
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <div
            className={classes.paper}
            style={{
              ...(isSuspended
                ? { height: "380px" }
                : { maxHeight: "760px" }),
            }}
          >
            <IconButton
              style={{
                position: "absolute",
                right: "10px",
                top: "10px",
                color: "#37474f",
                fontWeight: "bold",
                borderRadius: "100px",
                padding: 5,
                display: "none",
              }}
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
            {loading ? (
              <div
                style={{
                  display: "flex",
                  flex: 1,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress
                  style={{
                    color: "#E1A731",
                  }}
                />
              </div>
            ) : (
              <>
                <h1 style={{ fontWeight: "bold" }}>{modalTitle}</h1>
                <div
                  style={{
                    margin: "auto",
                    overflow: "scroll",
                    paddingLeft: "20px",
                    paddingRight: "20px",
                    ...(isSuspended ? { flex: 1 } : {}),
                  }}
                >
                  {isSuspended ? (
                    <div
                      style={{
                        display: "flex",
                        flex: 1,
                        flexDirection: "column",
                        overflow: "scroll",
                        alignItems: "center",
                        paddingRight: "25px",
                        paddingLeft: "25px",
                        paddingTop: "30px",
                        height: "100%",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <h3
                          style={{
                            textAlign: "left",
                            margin: 0,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {enumStrings.sendLoginModal.description}
                        </h3>
                      </div>
                      <Button
                        className={classes.yesButton}
                        onClick={handleClose}
                        variant="contained"
                      >
                        {enumStrings.shopLocal.createShopLocalCampaign.close}
                      </Button>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flex: 1,
                        flexDirection: "column",
                        overflow: "scroll",
                      }}
                    >
                      <p
                        style={{
                          fontWeight: "bold",
                          fontSize: "19px",
                          textAlign: "center",
                          width: "100%",
                          marginBottom: "10px",
                        }}
                      >
                        {enumStrings.sendLoginModal.option1Header}
                      </p>
                      <span>
                        <h3 style={{ textAlign: "left", margin: 0 }}>
                          {enumStrings.sendLoginModal.description2}{" "}
                          {"hello@chamberperksapp.com"}{" "}
                          {enumStrings.sendLoginModal.to} {memberCompanyName}{" "}
                          {enumStrings.sendLoginModal.at}{" "}
                          <span style={{ fontWeight: "bold" }}>
                            {removePlusAddress({
                              adminConsoleLoginEmail: memberAdminPortalEmail,
                              chamberName: chamberInfo?.name || "",
                            })}
                          </span>{" "}
                          {enumStrings.sendLoginModal.with}{" "}
                          {`${enumStrings.sendLoginModal.instruction2}`}
                          .
                        </h3>
                      </span>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          className={classes.yesButton}
                          onClick={executeSendLoginInfoEmail}
                          variant="contained"
                        >
                          {enumStrings.sendLoginModal.sendEmail}
                        </Button>
                        <span
                          onClick={() => {
                            setSampleLoginInfoEmailModalOpen(true);
                          }}
                          style={{
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          {enumStrings.sendLoginModal.preview}
                        </span>
                      </div>
                      <br />
                      <br />
                      <p
                        style={{
                          fontWeight: "bold",
                          marginBottom: "10px",
                          fontSize: "19px",
                          textAlign: "center",
                          width: "100%",
                        }}
                      >
                        {enumStrings.sendLoginModal.option2Header}
                      </p>
                      <span>
                        <h3 style={{ textAlign: "left", margin: 0 }}>
                          {enumStrings.sendLoginModal.downloadCredentialsInfo.replace(
                            "{memberCompanyName}",
                            memberCompanyName
                          )}{" "}
                          <span
                            onClick={() => {
                              setSampleLoginInfoEmailModalOpen(true);
                            }}
                            style={{
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            {enumStrings.sendLoginModal.clickHere}
                          </span>{" "}
                          {enumStrings.sendLoginModal.info2}
                        </h3>
                      </span>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          marginBottom: "-10px",
                        }}
                      >
                        <Button
                          className={classes.okButton}
                          onClick={executeDownloadLoginInfo}
                          variant="contained"
                        >
                          {enumStrings.sendLoginModal.downloadCredentials}
                        </Button>
                      </div>

                      <br />
                      <br />

                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
      <CSVLink
        data={csvData}
        filename={`${memberCompanyName} member dashboard credentials.csv`}
        id="login-info-modal-csv-download-button"
        style={{ display: "none" }}
      />
      <Snackbar
        open={Boolean(emailErrorMessage)}
        autoHideDuration={5000}
        onClose={() => setEmailErrorMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setEmailErrorMessage("")}
          severity="error"
          variant="filled"
          elevation={6}
        >
          {emailErrorMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default SendLoginInformationModal;
